import { useEffect, useRef, useState } from 'react';

import { DateOptions, DatePopperPlacement, Size } from 'coreOld/enums';
import { useCloseMenuIfClickedOutside } from 'coreOld/hooks';
import { IDateOption } from 'coreOld/types';
import { formatDate } from 'coreOld/utils';

import DateIntervalView from './DateIntervalView';

interface IProps {
  name: string;
  options: IDateOption[];
  value?: string | number;
  placeholder?: string;
  size?: Size;
  onChange?;
  rightPosition?: boolean;
  popperPlacement?: DatePopperPlacement;
}

const DateInterval = ({
  name,
  options,
  value,
  onChange,
  placeholder,
  size = Size.LG,
  rightPosition = false,
  popperPlacement,
}: IProps) => {
  const ref = useRef(null);

  const [selectedOption, setSelectedOption] = useState(null);
  const [opened, setOpened] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  useCloseMenuIfClickedOutside(ref, opened, () => setOpened(false));

  useEffect(() => {
    if (!value) {
      handleChange(options[0]);
    }
  }, [value]);

  const onChangeDate = (dates: Date[]) => {
    const [start, end] = dates;

    setStartDate(start);
    setEndDate(end);
  };

  const handleChange = (option: IDateOption) => {
    const label = options[option.id]?.label;
    const value = options[option.id]?.value;

    const filterValue = `${formatDate(value?.startDate)}|${formatDate(value?.endDate)}`;

    if (label === DateOptions.ALL) {
      setSelectedOption(null);
      setOpened(false);

      setStartDate(null);
      setEndDate(null);

      onChange('');

      return;
    }

    if (label === DateOptions.CUSTOM_RANGE) {
      setSelectedOption(option);

      return;
    }

    setSelectedOption(option);
    setStartDate(value.startDate);
    setEndDate(value.endDate);

    onChange(filterValue);
    setOpened(false);
  };

  const onApply = (startDate: Date, endDate: Date) => {
    const value = { startDate, endDate };
    const filterValue = `${formatDate(startDate)}|${formatDate(endDate)}`;

    const option = {
      id: options.length - 1,
      value,
      label: DateOptions.CUSTOM_RANGE,
    };

    handleChange(option);
    onChange(filterValue);
    setOpened(false);
  };

  return (
    <DateIntervalView
      {...{
        name,
        onApply,
        onChange: onChangeDate,
        handleChange,
        setOpened,
        opened,
        options,
        selectedOption,
        startDate,
        endDate,
        placeholder,
        size,
        rightPosition,
        popperPlacement,
        ref,
      }}
    />
  );
};

export default DateInterval;
